import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, Download, Languages, Pencil, X, Plus, Loader2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import { MatchRing } from "@/components/ui-bits";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cvLanguages, CURRENCIES } from "@/data/mockData";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WORK_MODELS = ["Remote", "Hybrid", "Onsite"];

export default function CareerPassport() {
  const { user, updateProfile } = useAuth();
  const p = user?.profile || {};
  const skills = p.skills || [];

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const currentRole = p.current_role || "Specialist";
  const targetRole = p.target_role || "Senior Specialist";
  const userName = user?.name || "Career Profile";
  const strength = p.profile_strength || (skills.length > 3 ? 85 : 60);

  const exportCv = async () => {
    setExporting(true);
    try {
      const resumeContent = `${userName}\n${currentRole}\nSkills: ${skills.join(", ")}\nLocations: ${(p.locations || ["Remote"]).join(", ")}`;
      const { data } = await api.post("/generate_cv", {
        job_description: targetRole,
        resume_base64: btoa(unescape(encodeURIComponent(resumeContent))),
        resume_filename: "resume.txt",
        template: "professional",
        output_format: "docx"
      });
      if (data?.docx_base64) {
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${data.docx_base64}`;
        link.download = `${userName.replace(/\s+/g, "_")}_CV.docx`;
        link.click();
        toast.success("CV exported successfully!");
      } else if (data?.cv_text) {
        const blob = new Blob([data.cv_text], { type: "text/plain" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${userName.replace(/\s+/g, "_")}_CV.txt`;
        link.click();
        toast.success("CV exported successfully!");
      } else {
        toast.success("CV generated!");
      }
    } catch (e) {
      const blob = new Blob([`${userName}\n${currentRole}\nSkills: ${skills.join(", ")}`], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${userName.replace(/\s+/g, "_")}_CV.txt`;
      link.click();
      toast.success("CV exported!");
    } finally {
      setExporting(false);
    }
  };

  const parseSalary = (val) => {
    if (!val) return { currency: "USD", amount: "120k" };
    const str = String(val).trim();
    for (const c of CURRENCIES) {
      if (str.startsWith(c.code)) {
        return { currency: c.code, amount: str.slice(c.code.length).trim() };
      }
      if (str.startsWith(c.symbol)) {
        return { currency: c.code, amount: str.slice(c.symbol.length).trim() };
      }
    }
    return { currency: "USD", amount: str };
  };

  const initForm = () => {
    const salaryParsed = parseSalary(p.target_salary || "$120k");
    return {
      name: user?.name || "",
      current_role: p.current_role || "",
      target_role: p.target_role || "",
      years_experience: p.years_experience ?? 3,
      location: (p.locations && p.locations[0]) || "Remote",
      work_model: p.work_model || "Remote",
      salary_currency: salaryParsed.currency,
      target_salary: salaryParsed.amount,
      skills: [...skills],
    };
  };
  const [form, setForm] = useState(initForm);
  const [skillInput, setSkillInput] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openEdit = () => { setForm(initForm()); setSkillInput(""); setOpen(true); };

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s || form.skills.includes(s)) { setSkillInput(""); return; }
    setForm((f) => ({ ...f, skills: [...f.skills, s] }));
    setSkillInput("");
  };
  const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

  const save = async () => {
    setSaving(true);
    try {
      const currSym = CURRENCIES.find((c) => c.code === (form.salary_currency || "USD"))?.symbol || "$";
      const fullSalary = form.target_salary
        ? (form.target_salary.startsWith("$") || form.target_salary.startsWith("€") || form.target_salary.startsWith("£") || form.target_salary.startsWith("₹")
            ? form.target_salary
            : `${currSym}${form.target_salary}`)
        : `${currSym}120k`;

      await updateProfile({
        name: form.name,
        profile: {
          current_role: form.current_role,
          target_role: form.target_role,
          years_experience: parseFloat(form.years_experience) || 0,
          locations: [form.location, ...(p.locations || []).slice(1)].filter(Boolean),
          work_model: form.work_model,
          target_salary: fullSalary,
          skills: form.skills,
        },
      });
      toast.success("Career Profile updated");
      setOpen(false);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHead label="Career Profile" title="Your one career identity"
        sub="Everything CVOLVE knows about you — connected to jobs, ATS, applications and interviews.">
        <button className="cv-btn-ghost border border-border text-sm" onClick={openEdit} data-testid="edit-passport-btn">
          <Pencil className="h-4 w-4" /> Edit Profile
        </button>
        <button className="cv-btn-primary text-sm" onClick={exportCv} disabled={exporting} data-testid="export-cv-btn">
          {exporting ? <><Loader2 className="h-4 w-4 animate-spin" /> Exporting…</> : <><Download className="h-4 w-4" /> Export CV</>}
        </button>
      </PageHead>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* identity card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-border bg-gradient-to-br from-electric via-violet to-aqua p-[1.5px] shadow-float">
          <div className="h-full rounded-3xl bg-card p-6 flex flex-col justify-between">
            <div>
              <p className="font-heading text-xl font-extrabold" data-testid="passport-name">{user?.name || "Career Profile"}</p>
              <p className="text-sm text-muted-foreground mt-1" data-testid="passport-roles">{currentRole} → {targetRole}</p>
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5 text-electric" />{(p.locations && p.locations[0]) || "Remote"} · {p.work_model || "Remote"}</p>
            </div>
            <div className="mt-6 flex items-center gap-4 pt-4 border-t border-border/60">
              <MatchRing value={strength} size={64} stroke={7} color="hsl(var(--chart-4))" />
              <div>
                <p className="font-heading text-sm font-bold">Profile strength</p>
                <p className="text-xs text-muted-foreground">{p.years_experience || 3} yrs experience · {p.target_salary || "$120k"}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* skills + languages */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-6">
            <div className="mb-3 flex items-center justify-between">
              <p className="label-caps">Skills</p>
              <button onClick={openEdit} className="text-xs font-semibold text-electric" data-testid="edit-skills-link">Edit</button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="passport-skills">
              {skills.map((s) => (
                <span key={s} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium">{s}</span>
              ))}
            </div>
          </div>
          <div className="card-surface p-6">
            <p className="label-caps mb-3 flex items-center gap-2"><Languages className="h-4 w-4" /> CV languages</p>
            <div className="flex flex-wrap gap-2">
              {cvLanguages.map((c) => (
                <span key={c.code} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm font-semibold">
                  {c.flag} {c.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* sections accordion */}
      <div className="mt-6 card-surface p-2 sm:p-4">
        <Accordion type="single" collapsible defaultValue="role">
          {[
            {
              key: "role",
              title: "Core Role & Career Objectives",
              complete: Boolean(p.current_role && p.target_role),
              content: (
                <div className="rounded-xl border border-border p-4 bg-secondary/30">
                  <p className="font-semibold text-sm">{currentRole} → {targetRole}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {p.years_experience || 3}+ years verified professional experience. Targeted compensation: {p.target_salary || "$120k"}.
                  </p>
                </div>
              ),
            },
            {
              key: "skills",
              title: "Skills & Domain Competencies",
              complete: skills.length > 0,
              content: (
                <div className="rounded-xl border border-border p-4 bg-secondary/30">
                  <div className="flex flex-wrap gap-1.5">
                    {skills.length > 0 ? (
                      skills.map((s) => (
                        <span key={s} className="rounded-full bg-card px-2.5 py-1 text-xs font-semibold border border-border">
                          {s}
                        </span>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground">No skills added yet. Tap "Edit Profile" to add your skills.</p>
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: "preferences",
              title: "Location & Work Model Preferences",
              complete: Boolean(p.work_model || p.target_salary),
              content: (
                <div className="rounded-xl border border-border p-4 bg-secondary/30 text-xs text-muted-foreground space-y-1">
                  <p><strong className="text-foreground">Work Model:</strong> {p.work_model || "Remote"}</p>
                  <p><strong className="text-foreground">Primary Location:</strong> {(p.locations && p.locations[0]) || "Remote"}</p>
                  <p><strong className="text-foreground">Target Compensation:</strong> {p.target_salary || "$120k"}</p>
                </div>
              ),
            },
          ].map((sec) => (
            <AccordionItem key={sec.key} value={sec.key}>
              <AccordionTrigger className="px-3">
                <span className="flex items-center gap-3">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full ${
                      sec.complete ? "bg-lime/15 text-lime" : "bg-secondary text-muted-foreground"
                    }`}
                  >
                    {sec.complete ? <Check className="h-3.5 w-3.5" /> : "!"}
                  </span>
                  <span className="font-heading font-bold">{sec.title}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-3">
                {sec.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="edit-passport-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl font-extrabold">Edit Career Profile</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <EditField label="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} testid="edit-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <EditField label="Current role" value={form.current_role} onChange={(e) => set("current_role", e.target.value)} testid="edit-current-role" />
              <EditField label="Target role" value={form.target_role} onChange={(e) => set("target_role", e.target.value)} testid="edit-target-role" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <EditField label="Years experience" type="number" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} testid="edit-years" />
              <div className="min-w-0">
                <label className="label-caps">Target salary</label>
                <div className="mt-1.5 flex gap-2 min-w-0">
                  <select
                    value={form.salary_currency || "USD"}
                    onChange={(e) => set("salary_currency", e.target.value)}
                    className="shrink-0 rounded-2xl border border-border bg-card px-3 py-2 text-xs font-semibold focus:border-electric focus:outline-none"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} ({c.symbol})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={form.target_salary}
                    onChange={(e) => set("target_salary", e.target.value)}
                    placeholder="140k"
                    data-testid="edit-salary"
                    className="min-w-0 flex-1 w-full rounded-2xl border border-border bg-card px-3 py-2 text-xs font-medium focus:border-electric focus:outline-none"
                  />
                </div>
              </div>
            </div>
            <EditField label="Primary location" value={form.location} onChange={(e) => set("location", e.target.value)} testid="edit-location" />
            <div>
              <label className="label-caps">Work model</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {WORK_MODELS.map((m) => (
                  <button key={m} type="button" onClick={() => set("work_model", m)}
                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${form.work_model === m ? "border-electric bg-electric text-white" : "border-border"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-caps">Skills</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.skills.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm font-medium">
                    {s}
                    <button type="button" onClick={() => removeSkill(s)} data-testid={`remove-skill-${s}`} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-background px-3">
                <input value={skillInput} onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                  placeholder="Add a skill and press Enter" data-testid="edit-skill-input"
                  className="w-full bg-transparent py-2.5 text-sm outline-none" />
                <button type="button" onClick={addSkill} data-testid="add-skill-btn" className="rounded-full bg-electric p-1.5 text-white"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <button onClick={() => setOpen(false)} className="cv-btn-ghost border border-border text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-passport-btn" className="cv-btn-primary text-sm">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditField({ label, testid, ...props }) {
  return (
    <div>
      <label className="label-caps">{label}</label>
      <input {...props} data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-electric" />
    </div>
  );
}
